
import { useState, useEffect, Suspense } from "react";
import { Header } from "@/components/layout/Header";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { NotificationsCard } from "@/components/settings/NotificationsCard";
import { LegalLinksGrid } from "@/components/settings/LegalLinksGrid";
import { Toaster } from "@/components/ui/toaster";
import { AccountSettingsCard } from "@/components/settings/AccountSettingsCard";
import { SubscriptionManager } from "@/components/settings/subscription/SubscriptionManager";
import { useStripeInitializer } from "@/components/settings/stripe/StripeInitializer";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

// Component for displaying each skeleton card in the loading state
const SkeletonCard = ({ height }: { height: string }) => (
  <Card className="overflow-hidden">
    <CardHeader>
      <Skeleton className="h-7 w-1/3" />
    </CardHeader>
    <CardContent className={`space-y-3 ${height}`}>
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-2/3" />
      <Skeleton className="h-4 w-3/4" />
    </CardContent>
  </Card>
);

// Component for the subscription skeleton card
const SubscriptionSkeleton = () => (
  <Card>
    <CardHeader>
      <Skeleton className="h-7 w-28" />
    </CardHeader>
    <CardContent className="space-y-6">
      <div className="bg-gray-100 p-4 rounded mb-6">
        <Skeleton className="h-6 w-2/3 mx-auto" />
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="relative">
            <CardHeader className="pb-8 text-center">
              <Skeleton className="h-6 w-24 mx-auto mb-4" />
              <Skeleton className="h-8 w-32 mx-auto mt-4" />
              <Skeleton className="h-4 w-40 mx-auto mt-2" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-10 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
    </CardContent>
  </Card>
);

// Component for legal links grid skeleton
const LegalLinksSkeleton = () => (
  <div className="grid gap-6 md:grid-cols-3">
    {[1, 2, 3].map((i) => (
      <Card key={i} className="overflow-hidden">
        <CardHeader>
          <Skeleton className="h-5 w-1/2" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-4 w-full" />
        </CardContent>
      </Card>
    ))}
  </div>
);

export const Settings = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const stripePromise = useStripeInitializer();
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [contentVisible, setContentVisible] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        setIsAuthenticated(!!session);
        
        // Small initial delay to ensure layout is ready
        setTimeout(() => {
          setIsLoading(false);
          
          // Add a tiny delay before showing content to ensure smooth transition
          setTimeout(() => {
            setContentVisible(true);
          }, 50);
        }, 300);
      } catch (error) {
        console.error("Error checking authentication:", error);
        setIsLoading(false);
        setContentVisible(true);
      }
    };

    checkAuth();
  }, []);

  // Loading skeleton UI
  const renderLoadingState = () => (
    <div className="grid gap-6 animate-fade-in">
      <SubscriptionSkeleton />
      <SkeletonCard height="h-48" />
      <SkeletonCard height="h-40" />
      <LegalLinksSkeleton />
    </div>
  );

  if (!isAuthenticated && !isLoading) {
    navigate('/login');
    return null;
  }

  return (
    <>
      <div className="min-h-screen bg-gray-50">
        <Header />
        <main className="container mx-auto px-4 py-4 sm:py-8">
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            className="mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Dashboard
          </Button>

          <h1 className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8">{t("common.settings")}</h1>
          
          <div className={`transition-opacity duration-300 ${contentVisible ? 'opacity-100' : 'opacity-0'}`}>
            {isLoading ? (
              renderLoadingState()
            ) : (
              <div className="grid gap-6">
                <NotificationsCard />
                <SubscriptionManager />
                <AccountSettingsCard />
                <LegalLinksGrid />
              </div>
            )}
          </div>
        </main>
      </div>
      <Toaster />
    </>
  );
};

export default Settings;
